import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { SelectOption } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  DatabaseChangeType,
  MultiValueChangeType,
} from '@databases/utils/database/changesets';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { getGroupField } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';
import { getNewOption } from '../../SelectPicker';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    height: 40px;
  }
`;

const StyledButton = styled(Button)`
  && {
    height: 40px;
    width: 40px;
  }

  svg {
    height: 16px;
    width: auto;
  }
`;

interface BoardLayoutAddOptionButtonProps {
  contentId: string;
  viewSettings: DatabaseViewSettings;
}

const BoardLayoutAddOptionButton: FunctionComponent<BoardLayoutAddOptionButtonProps> = ({
  contentId,
  viewSettings,
}) => {
  const scope = useScope();
  const dispatch = defaultStoreUtils.useDispatch();
  const { showPanel, isPanelVisible } = usePanel();
  const { formatMessage } = useIntl();
  const [groupFieldId] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const groupField = getGroupField(databaseDraft, viewSettings.layout)!;
    return [groupField.id] as const;
  });

  const value = defaultStoreUtils.useSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const field = getDatabaseFieldRequired(database, groupFieldId);
    return field.settings.selectOptions;
  });

  const getNewValue = useCallback(() => getNewOption(value), [value]);

  const handleAddNewItem = useCallback(
    (value: SelectOption, allValues: SelectOption[]) => {
      const defaultLabel = formatMessage(messages['board-layout.default-tag-label'], {
        number: allValues.length,
      });

      const defaultLabelValue = {
        ...value,
        id: value.id ?? value.label,
        label: defaultLabel,
      };

      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.SET_SELECT_OPTIONS,
          fieldId: groupFieldId,
          newValue: [...allValues, defaultLabelValue],
          change: {
            type: MultiValueChangeType.APPEND,
            value: defaultLabelValue,
          },
        })
      );
    },
    [contentId, dispatch, groupFieldId, scope, formatMessage]
  );

  const handleClick = useCallback(() => {
    const newValue = getNewValue();
    const allValues = [...value, newValue];

    handleAddNewItem(newValue, allValues);
    if (!isPanelVisible('FieldSettingsPanel')) {
      showPanel('FieldSettingsPanel', { fieldId: groupFieldId });
    }
  }, [showPanel, isPanelVisible, groupFieldId, getNewValue, handleAddNewItem, value]);

  return (
    <StyledSkeletonItem isButton>
      <StyledButton
        onClick={handleClick}
        iconBefore={
          <AddIcon label={formatMessage(messages['layout.board-add-option'])} />
        }
      />
    </StyledSkeletonItem>
  );
};

export default BoardLayoutAddOptionButton;
